import {
  BankOutlined,
  CloseOutlined,
  ControlOutlined,
  LineChartOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import compact from 'lodash/compact'
import React, { useState, type JSX, type ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, NavLink } from 'react-router-dom'

import { CAPABILITIES } from 'constants/capabilities'
import { ROUTES } from 'constants/routes'
import { useHasCapability } from 'hooks/useCapabilities'
import { resetAuth } from 'redux/auth/actions'
import { closeSidebar, collapseSidebar, uncollapseSidebar } from 'redux/sidebar/actions'
import { resetSnackbar } from 'redux/snackbar/actions'
import { Dialog } from 'shared/components/base'
import { AdminGlyph, CommunityLogo } from 'shared/components/glyph'
import './Sidebar.scss'

export type LinkType = {
  icon: ReactNode
  text: string
  to: string
  badge?: string
  onClick?(e: React.MouseEvent): any
}

export const Sidebar = (): JSX.Element => {
  const dispatch = useDispatch()
  const canDeleteMember = useHasCapability(CAPABILITIES.ADMIN.FANS.WRITE)
  // @ts-expect-error TS(2339): Property 'snackbar' does not exist on type 'Defaul... Remove this comment to see the full error message
  const hasSnackbar = useSelector((s) => s.snackbar.items.length > 0)
  // @ts-expect-error TS(2339): Property 'sidebar' does not exist on type 'Default... Remove this comment to see the full error message
  const collapsed = useSelector((s) => s.sidebar.collapsed)
  // @ts-expect-error TS(2339): Property 'sidebar' does not exist on type 'Default... Remove this comment to see the full error message
  const opened = useSelector((s) => s.sidebar.opened)
  const [loggingOut, setLoggingOut] = useState(false)

  const canViewSSOConfigs = useHasCapability(CAPABILITIES.ADMIN.SSO_CONFIGS.READ)

  const links: LinkType[] = compact([
    { icon: <LineChartOutlined />, text: 'Activity', to: ROUTES.ACTIVITY, badge: 'Beta' },
    { icon: <BankOutlined />, text: 'Clients', to: ROUTES.CLIENTS },
    { icon: <SafetyOutlined />, text: 'Admins', to: ROUTES.ADMINS },
    { icon: <ControlOutlined />, text: 'Capabilities', to: ROUTES.CAPABILITIES.ROOT },
    canDeleteMember ? { icon: <SearchOutlined />, text: 'Member', to: ROUTES.MEMBER } : null,
    canViewSSOConfigs ? { icon: <LockOutlined />, text: 'SSO Configs', to: ROUTES.SSO_CONFIGS } : null,
    {
      icon: <LogoutOutlined />,
      text: 'Logout',
      to: '/login',
      onClick: (e: any) => {
        e.preventDefault()
        setLoggingOut(true)
      },
    },
  ])

  const onClose = () => {
    if (opened) dispatch(closeSidebar())
  }

  const onLinkClick = () => {
    if (hasSnackbar) dispatch(resetSnackbar())
    onClose()
  }

  const toggleCollapse = () => {
    if (collapsed) {
      dispatch(uncollapseSidebar())
    } else {
      dispatch(collapseSidebar())
    }
  }

  const cname = 'Sidebar-root' + (collapsed ? ' collapsed' : '')

  return (
    <div className={cname}>
      <div className="Sidebar-main">
        <div className="Sidebar-header">
          <Link className="Sidebar-header-logo" onClick={onLinkClick} to="/">
            <CommunityLogo color="#111111" height="18px" />
          </Link>
          <AdminGlyph />
          <div className="Sidebar-header-close" onClick={onClose}>
            <div className="Sidebar-header-close-icon">
              <CloseOutlined />
            </div>
          </div>
        </div>
        <div className="Sidebar-menu">
          <div className="Sidebar-menu-slide">
            {links.map((item) => {
              const { icon, text, onClick, badge, ...rest } = item
              return (
                <NavLink
                  aria-label={text}
                  className="Sidebar-menu-item"
                  key={item.to}
                  onClick={(e) => (onClick ? onClick(e) : onClose())}
                  title={collapsed ? text : undefined}
                  {...rest}
                >
                  <div aria-hidden="true" className="Sidebar-menu-item-icon">
                    {icon}
                  </div>
                  <div className="Sidebar-menu-item-text">{text}</div>
                  {badge && <span className="Sidebar-menu-item-badge">{badge}</span>}
                </NavLink>
              )
            })}
          </div>
        </div>
        <div className="Sidebar-footer">
          <button
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            className="Sidebar-collapse"
            onClick={toggleCollapse}
            type="button"
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>
        </div>
        {loggingOut && (
          <Dialog
            actions={[
              {
                text: 'Cancel',
                onClick: () => setLoggingOut(false),
              },
              {
                text: 'Logout',
                solid: true,
                onClick: () => dispatch(resetAuth()),
              },
            ]}
            onOuterClick={() => setLoggingOut(false)}
          >
            Are you sure you want to logout?
          </Dialog>
        )}
      </div>
    </div>
  )
}

export default Sidebar
